import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import {
  DEFAULT_NEAR_EXPIRY_DAYS,
  INVENTORY_FILTER_OPTIONS,
  InventoryFilterKey,
  InventoryFiltersState,
  MAX_NEAR_EXPIRY_DAYS,
  MIN_NEAR_EXPIRY_DAYS,
  cloneFilters,
  countActiveFilters,
} from './inventoryFilters';

interface InventoryFilterPanelProps {
  filters: InventoryFiltersState;
  onApply: (filters: InventoryFiltersState) => void;
}

const GROUP_LABELS: Record<string, string> = {
  stock: 'Stock & alerts',
  status: 'Status',
};

const InventoryFilterPanel: React.FC<InventoryFilterPanelProps> = ({ filters, onApply }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<InventoryFiltersState>(() => cloneFilters(filters));

  const appliedCount = countActiveFilters(filters);

  useEffect(() => {
    if (!isOpen) {
      setDraft(cloneFilters(filters));
    }
  }, [filters, isOpen]);

  const openModal = () => setIsOpen(true);

  const closeModal = () => setIsOpen(false);

  const toggleDraft = useCallback((key: InventoryFilterKey) => {
    setDraft((prev) => {
      const next = cloneFilters(prev);
      if (key === 'activeOnly' && !prev.activeOnly) {
        delete next.inactive;
      }
      if (key === 'inactive' && !prev.inactive) {
        delete next.activeOnly;
      }
      if (next[key]) {
        delete next[key];
        if (key === 'nearExpiry') {
          delete next.nearExpiryDays;
        }
      } else {
        next[key] = true;
        if (key === 'nearExpiry' && next.nearExpiryDays == null) {
          next.nearExpiryDays = DEFAULT_NEAR_EXPIRY_DAYS;
        }
      }
      return next;
    });
  }, []);

  const handleNearExpiryDaysChange = (value: string) => {
    const parsed = parseInt(value, 10);
    if (Number.isNaN(parsed)) return;
    const clamped = Math.min(MAX_NEAR_EXPIRY_DAYS, Math.max(MIN_NEAR_EXPIRY_DAYS, parsed));
    setDraft((prev) => ({ ...prev, nearExpiryDays: clamped }));
  };

  const handleApply = () => {
    const toApply = cloneFilters(draft);
    if (toApply.nearExpiry && toApply.nearExpiryDays == null) {
      toApply.nearExpiryDays = DEFAULT_NEAR_EXPIRY_DAYS;
    }
    onApply(toApply);
    closeModal();
  };

  const handleResetDraft = () => setDraft({});

  const groups = ['stock', 'status'] as const;

  return (
    <>
      <Button
        color="light"
        className={`d-inline-flex align-items-center gap-2 border ${
          appliedCount > 0 ? 'border-primary bg-primary-subtle text-primary' : ''
        }`}
        style={{ height: 42 }}
        onClick={openModal}
        aria-label="Filter inventory"
      >
        <i className="bx bx-filter-alt fs-5" />
        <span className="d-none d-sm-inline">Filters</span>
        {appliedCount > 0 && (
          <Badge color="primary" pill className="ms-1">
            {appliedCount}
          </Badge>
        )}
      </Button>

      <Modal isOpen={isOpen} toggle={closeModal} centered scrollable size="md">
        <ModalHeader toggle={closeModal} className="border-bottom">
          <i className="bx bx-filter-alt me-2 text-primary" />
          Filter inventory
        </ModalHeader>

        <ModalBody className="p-0">
          <div className="d-flex justify-content-end px-3 pt-2">
            {countActiveFilters(draft) > 0 && (
              <Button color="link" size="sm" className="p-0 text-muted" onClick={handleResetDraft}>
                Reset all
              </Button>
            )}
          </div>

          {groups.map((group) => (
            <div key={group}>
              <div className="px-3 pt-2 pb-1 text-muted small fw-bold text-uppercase">
                {GROUP_LABELS[group]}
              </div>
              {INVENTORY_FILTER_OPTIONS.filter((o) => o.group === group).map((option) => {
                const selected = !!draft[option.key];
                const inputId = `inv-filter-${option.key}`;
                return (
                  <div key={option.key}>
                    <div className={`px-3 py-2 ${selected ? 'bg-primary-subtle' : ''}`}>
                      <FormGroup check className="mb-0">
                        <Input
                          type="checkbox"
                          id={inputId}
                          checked={selected}
                          onChange={() => toggleDraft(option.key)}
                        />
                        <Label
                          check
                          for={inputId}
                          className="d-flex gap-2 align-items-start w-100"
                        >
                          <span
                            className={`rounded d-flex align-items-center justify-content-center flex-shrink-0 ${
                              selected ? 'bg-primary text-white' : 'bg-light text-secondary'
                            }`}
                            style={{ width: 32, height: 32 }}
                          >
                            <i className={`bx ${option.icon}`} />
                          </span>
                          <span>
                            <span className="d-block fw-semibold small">{option.label}</span>
                            <span className="d-block text-muted" style={{ fontSize: '0.75rem' }}>
                              {option.description}
                            </span>
                          </span>
                        </Label>
                      </FormGroup>
                    </div>

                    {option.key === 'nearExpiry' && draft.nearExpiry && (
                      <div className="px-3 pb-3 ps-5">
                        <Label for="near-expiry-days" className="small text-muted mb-1">
                          Expiring within (days)
                        </Label>
                        <div className="d-flex align-items-center gap-2">
                          <Input
                            id="near-expiry-days"
                            type="number"
                            min={MIN_NEAR_EXPIRY_DAYS}
                            max={MAX_NEAR_EXPIRY_DAYS}
                            value={draft.nearExpiryDays ?? DEFAULT_NEAR_EXPIRY_DAYS}
                            onChange={(e) => handleNearExpiryDaysChange(e.target.value)}
                            bsSize="sm"
                            style={{ maxWidth: 100 }}
                          />
                          <span className="text-muted small">days from today</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </ModalBody>

        <ModalFooter className="border-top bg-light">
          <Button color="secondary" outline onClick={closeModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleApply}>
            Apply filters
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default InventoryFilterPanel;
